import { useState, useRef, useEffect, useCallback } from 'react';
import { projectsApi, type AddressCandidate } from '../../lib/apiClient';

/**
 * Job site address — shows what is stored, and lets someone replace it by
 * searching and picking a real place.
 *
 * WHY THIS REPLACED A TEXT BOX. `job_address` was one free-text field, and three
 * features read a location out of it: weather, the manpower gap analysis, and
 * travel per-diem mileage. Measured on production 2026-09-11, a quarter of
 * travel-job addresses could not produce a reliable answer, and three had no
 * city or state at all — one of which a geocoder happily resolves to Oklahoma.
 *
 * ⚠️ THE INPUT IS A SEARCH BOX, NEVER THE VALUE. That is the whole shape of this
 * component and it is deliberate. An earlier version put the committed address
 * INTO an editable field, making it two things at once, and produced a run of
 * defects that were all the same question wearing different hats (#479):
 * editing the text meant nothing but looked like it meant something; emptying it
 * hid the Clear button and left a verified address stored with no control to
 * remove it; a failed save left unsaved text sitting under a "Verified location"
 * label.
 *
 * Now the stored address is TEXT — changed only by picking, removed only by a
 * button. Typing cannot express an address at all, only search for one, so there
 * is no state in which what is on screen disagrees with what is stored.
 *
 * ⚠️ EVERY SEARCH IS A BILLABLE AZURE MAPS TRANSACTION. A 450ms debounce and a
 * four-character floor keep typing an address to a handful of calls rather than
 * thirty, and the search box only exists while somebody is actively changing the
 * address — so viewing a project costs nothing at all.
 *
 * ⚠️ The coordinates travel with the pick. The user approves a specific place on
 * screen and that exact place is stored; re-geocoding on save would be a second
 * billable call that can return a different answer from the one they looked at.
 */

const DEBOUNCE_MS = 450;
const MIN_QUERY = 4;

const css = `
  .jap { display: flex; flex-direction: column; gap: var(--ew-s-2); }

  .jap-current { font-size: var(--ew-fs-body-sm); color: var(--ew-dark); word-break: break-word; }
  .jap-empty { font-size: var(--ew-fs-body-sm); color: var(--ew-n-400); font-style: italic; }

  .jap-actions { display: flex; gap: var(--ew-s-2); flex-wrap: wrap; }
  .jap-btn {
    padding: 6px var(--ew-s-3);
    border: 1px solid var(--ew-n-200);
    background: #fff;
    border-radius: var(--ew-r-md);
    font: inherit;
    font-size: var(--ew-fs-body-sm);
    color: var(--ew-dark);
    cursor: pointer;
    min-height: 32px;
  }
  .jap-btn:hover:not(:disabled) { background: var(--ew-n-50); }
  .jap-btn:disabled { cursor: not-allowed; opacity: 0.6; }
  .jap-btn-danger { color: var(--ew-danger); border-color: currentColor; }

  .jap-search { position: relative; }

  /* ⚠️ ITS OWN INPUT RULE, not OverviewEdit's .ov-input. This component is now
     mounted on TWO routes — the project workspace and Admin → Job Addresses —
     and a module-prefixed class only exists while its declaring component is
     mounted. Borrowing .ov-input rendered a bare, unstyled search box on the
     admin screen; styles/cssCoPresence.spec.ts is what caught it. */
  .jap-input {
    border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 7px 10px;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); width: 100%;
    background: #fff; color: var(--ew-dark); font-family: inherit;
  }
  .jap-menu {
    position: absolute;
    z-index: 40;
    top: calc(100% + 4px);
    left: 0; right: 0;
    background: #fff;
    border: 1px solid var(--ew-n-200);
    border-radius: var(--ew-r-md);
    box-shadow: var(--ew-shadow-2);
    max-height: 280px;
    overflow-y: auto;
  }
  .jap-opt {
    display: block;
    width: 100%;
    text-align: left;
    padding: 10px var(--ew-s-3);
    border: 0;
    background: none;
    cursor: pointer;
    font: inherit;
    font-size: var(--ew-fs-body-sm);
    border-bottom: 1px solid var(--ew-n-100);
  }
  .jap-opt:last-child { border-bottom: 0; }
  .jap-opt:hover:not(:disabled), .jap-opt[data-active='true']:not(:disabled) {
    background: var(--ew-green-50);
  }
  .jap-opt:disabled { cursor: not-allowed; opacity: 0.65; }
  .jap-opt-coarse {
    display: inline-block;
    margin-left: var(--ew-s-2);
    font-size: var(--ew-fs-meta);
    color: var(--ew-performance);
    border: 1px solid currentColor;
    border-radius: var(--ew-r-pill);
    padding: 0 6px;
  }
  .jap-note { padding: 10px var(--ew-s-3); font-size: var(--ew-fs-body-sm); color: var(--ew-n-500); }

  .jap-status { font-size: var(--ew-fs-meta); display: flex; align-items: center; gap: 6px; }
  .jap-dot { width: 7px; height: 7px; border-radius: var(--ew-r-pill); flex-shrink: 0; }
  .jap-verified     { color: var(--ew-green); }
  .jap-verified     .jap-dot { background: var(--ew-green); }
  .jap-unverified   { color: var(--ew-n-500); }
  .jap-unverified   .jap-dot { background: var(--ew-n-400); }
  .jap-unresolvable { color: var(--ew-danger); }
  .jap-unresolvable .jap-dot { background: var(--ew-danger); }
`;

export type PickerStatus = 'unverified' | 'verified' | 'unresolvable';

export interface JobAddressPickerProps {
  /** The address as currently stored. Display only — never editable. */
  value: string;
  /** Verification state of the stored value. */
  status: PickerStatus;
  /** Commit a picked address. A rejection surfaces an error and changes nothing. */
  onPick: (picked: AddressCandidate) => void | Promise<void>;
  /**
   * Remove the stored address. A rejection surfaces an error and changes nothing.
   *
   * ⚠️ OMIT IT TO HIDE THE REMOVE BUTTON ENTIRELY. The admin worklist does, for
   * two reasons. Clearing is unguarded — it writes by project id with no
   * expectation — so a stale worklist row could wipe an address a PM verified
   * while the list sat open, taking the per-diem coordinates with it. And a
   * worklist row shows only a job number and some text: destroying an address
   * from there means doing it without the context that makes it a sane decision.
   * The row links to the job, which is where removing belongs. (Codex, #479.)
   */
  onClear?: () => void | Promise<void>;
  disabled?: boolean;
}

export default function JobAddressPicker({
  value, status, onPick, onClear, disabled,
}: JobAddressPickerProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<AddressCandidate[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The query whose response is still wanted. Anything else is stale. */
  const wanted = useRef('');

  const closeSearch = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    wanted.current = '';
    setSearchOpen(false);
    setQuery('');
    setCandidates(null);
    setActiveIdx(-1);
    setSearching(false);
    setSearchFailed(false);
  }, []);

  // A save landing, or a different project, closes the search.
  useEffect(() => { closeSearch(); }, [value, closeSearch]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const runSearch = useCallback(async (q: string) => {
    wanted.current = q;
    setSearchFailed(false);
    setSearching(true);
    try {
      const res = await projectsApi.searchAddress(q);
      if (wanted.current !== q) return;
      // ⚠️ ONLY A SERVICE PROBLEM IS A SERVICE PROBLEM. `Boolean(res.reason)`
      // also caught 'no_match', which is Azure answering successfully that it
      // found nothing — the ordinary case for a typo or an address with no town.
      // That told the user to wait and retry when what they needed was to fix
      // the address. The two messages give opposite advice, so conflating them
      // is worse than having only one. (Codex, #479.)
      setSearchFailed(res.reason === 'service_error' || res.reason === 'not_configured');
      setCandidates(res.candidates);
      setActiveIdx(res.candidates.length ? 0 : -1);
    } catch {
      if (wanted.current !== q) return;
      // A thrown request is a service failure, not a verdict on the address.
      setSearchFailed(true);
      setCandidates([]);
    } finally {
      if (wanted.current === q) setSearching(false);
    }
  }, []);

  const onType = (text: string) => {
    setQuery(text);
    if (timer.current) clearTimeout(timer.current);

    const trimmed = text.trim();
    // ⚠️ Retire the previous RESULTS and the previous REQUEST the moment the
    // query changes. Leaving the old menu up let someone click a result for a
    // query they had already typed over. (Codex, #479.)
    wanted.current = trimmed;
    setCandidates(null);
    setActiveIdx(-1);
    setSearchFailed(false);

    if (trimmed.length < MIN_QUERY) {
      setSearching(false);
      return;
    }
    timer.current = setTimeout(() => runSearch(trimmed), DEBOUNCE_MS);
  };

  /** Run a mutation, serialized, with any failure shown rather than swallowed. */
  const commit = async (fn: () => void | Promise<void>, failure: string) => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await fn();
      closeSearch();
    } catch {
      setError(failure);
    } finally {
      setSaving(false);
    }
  };

  const pick = (c: AddressCandidate) => {
    // ⚠️ A coarse result is listed but not selectable — a city centroid is not a
    // job site, and mileage from the middle of Columbus is wrong in a way nobody
    // would notice. The API refuses it too; this only saves a confusing error.
    //
    // ⚠️ Branch on `selectable`, NOT on `type`. Azure returns type 'Address' for
    // a postcode centroid it fell back to, distinguishable only by matchCodes —
    // which the API checks so this does not have to. (Codex, #479.)
    if (!c.selectable) return;
    void commit(() => onPick(c), 'That address could not be saved. Nothing was changed — try again.');
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { closeSearch(); return; }
    if (!candidates?.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % candidates.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + candidates.length) % candidates.length);
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      pick(candidates[activeIdx]);
    }
  };

  const statusLabel =
    status === 'verified' ? 'Verified location'
    : status === 'unresolvable' ? 'Could not be located — search and pick the site'
    : value.trim() ? 'Not yet verified' : 'No address';

  return (
    <div className="jap">
      <style>{css}</style>

      {value.trim()
        ? <div className="jap-current">{value}</div>
        : <div className="jap-empty">No address set</div>}

      {error ? (
        <div className="jap-status jap-unresolvable" role="alert">
          <span className="jap-dot" />
          <span>{error}</span>
        </div>
      ) : (
        <div className={`jap-status jap-${status}`}>
          <span className="jap-dot" />
          <span>{statusLabel}</span>
        </div>
      )}

      {searchOpen ? (
        <div className="jap-search">
          <input
            className="jap-input"
            value={query}
            autoFocus
            disabled={disabled || saving}
            placeholder="Search for the site address…"
            onChange={(e) => onType(e.target.value)}
            onKeyDown={onKeyDown}
            role="combobox"
            aria-expanded={Boolean(candidates?.length)}
            aria-autocomplete="list"
            aria-label="Search for the job site address"
          />

          {searching || candidates ? (
            <div className="jap-menu" role="listbox">
              {searching ? <div className="jap-note">Searching…</div> : null}
              {!searching && candidates?.length === 0 ? (
                <div className="jap-note">
                  {searchFailed
                    ? 'Address lookup is unavailable right now. Your address may be fine — try again in a moment.'
                    : 'No match. Check the street, town and state — an address without a town cannot be located.'}
                </div>
              ) : null}
              {candidates?.map((c, i) => (
                <button
                  key={`${c.formatted}-${c.latitude}-${c.longitude}`}
                  type="button"
                  className="jap-opt"
                  disabled={!c.selectable || saving}
                  data-active={i === activeIdx}
                  role="option"
                  aria-selected={i === activeIdx}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => pick(c)}
                >
                  <span>{c.formatted}</span>
                  {!c.selectable
                    ? <span className="jap-opt-coarse">Not a street address</span>
                    : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="jap-actions">
        {searchOpen ? (
          <button type="button" className="jap-btn" onClick={closeSearch} disabled={saving}>
            Cancel
          </button>
        ) : (
          <button
            type="button"
            className="jap-btn"
            disabled={disabled || saving}
            onClick={() => { setError(null); setSearchOpen(true); }}
          >
            {value.trim() ? 'Change address' : 'Set address'}
          </button>
        )}

        {/* ⚠️ Removing is its own control, reachable whenever an address exists.
            It used to be a Clear button inside the text field, which vanished
            the moment the field was emptied — leaving a stored address and no
            way to remove it. (Codex, #479.) */}
        {onClear && value.trim() && !searchOpen ? (
          <button
            type="button"
            className="jap-btn jap-btn-danger"
            disabled={disabled || saving}
            onClick={() => void commit(
              onClear,
              'That address could not be removed. Nothing was changed — try again.',
            )}
          >
            Remove
          </button>
        ) : null}
      </div>
    </div>
  );
}
